// File: frontend/src/utils/shareUtils.ts

import { EntryModel } from "../models";
import { generateMatchUrlFromEntry } from "./slugGenerator";

import { customTopicUrl, personUrl, clubUrl, teamUrl, entryPermalinkPath } from "./canonicalUrl";
/**
 * Shared helpers for the "paylaş" (share) menus used by the entry toolbar
 * (topic level) and by EntryRow (single entry level).
 *
 * Everything here is deliberately framework-free so both the toolbar and the
 * entry row can reuse it without duplicating URL/clipboard logic.
 */

/**
 * Converts a root-relative path ("/gundem/foo") into a fully qualified URL
 * ("https://host/gundem/foo"). Absolute URLs are returned untouched so callers
 * can pass either form.
 */
export function toAbsoluteUrl(url: string): string {
  if (!url) return window.location.href;
  if (/^https?:\/\//i.test(url)) return url;
  const path = url.startsWith("/") ? url : `/${url}`;
  return `${window.location.origin}${path}`;
}

/**
 * Copies text to the clipboard.
 * navigator.clipboard is only available in secure contexts (HTTPS), so the
 * execCommand fallback keeps this working on plain-HTTP QA environments and
 * on older browsers. Returns true when the copy succeeded.
 */
export async function copyTextToClipboard(text: string): Promise<boolean> {
  // Primary: Clipboard API (requires HTTPS / secure context)
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to the execCommand fallback below
    }
  }

  // Fallback: execCommand (works on HTTP and older browsers)
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.top = "0";
  textarea.style.left = "0";
  textarea.style.opacity = "0";
  textarea.style.pointerEvents = "none";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  try {
    return document.execCommand("copy");
  } catch (err) {
    console.error("Failed to copy to clipboard:", err);
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
}

/** Social network share endpoints — all of them accept a plain GET URL. */
export function buildWhatsappShareUrl(url: string, title: string): string {
  return `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`.trim())}`;
}

export function buildTwitterShareUrl(url: string, title: string): string {
  return `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;
}

export function buildFacebookShareUrl(url: string): string {
  return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
}

export function buildTelegramShareUrl(url: string, title: string): string {
  return `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;
}

/**
 * Opens a share endpoint in a new tab. noopener/noreferrer keeps the opened
 * page from getting a handle on our window.
 */
export function openShareWindow(href: string): void {
  window.open(href, "_blank", "noopener,noreferrer");
}

/**
 * Builds the base page URL (WITHOUT the #entry-{id} fragment) that an entry
 * lives on, from the entry's own topic fields. Exactly one of the five topic
 * FKs is set on any entry.
 *
 * Falls back to the current pathname when the entry does not carry enough
 * slug information (e.g. team entries loaded on the team page itself, where
 * the club slug is not part of the payload) — on that page the current
 * pathname is already the correct topic URL.
 */
export function buildEntryPageUrl(entry: EntryModel): string {
  // Canonical, id-terminated forms — the same ones the pages themselves
  // advertise. These used to be built from the slug alone, which still
  // resolved but cost every shared link a 301 round trip and, for teams,
  // handed out the retired two-segment address. Resolution goes through the
  // trailing id, so a link shared today survives the topic being renamed.
  if (entry.customTopicSlug && entry.customTopicId) {
    return customTopicUrl(entry.customTopicSlug, entry.customTopicId);
  }
  if (entry.personSlug && entry.personId) {
    return personUrl(entry.personSlug, entry.personId);
  }
  if (entry.teamSlug && entry.clubSlug && entry.teamId) {
    return teamUrl(entry.clubSlug, entry.teamSlug, entry.teamId);
  }
  if (entry.clubSlug && entry.clubId) {
    return clubUrl(entry.clubSlug, entry.clubId);
  }
  if (entry.matchId && entry.homeTeam && entry.awayTeam && entry.matchDate) {
    return generateMatchUrlFromEntry(
      entry.homeTeam,
      entry.awayTeam,
      new Date(entry.matchDate),
      entry.matchId
    );
  }
  return window.location.pathname;
}

/**
 * Stable permalink of a single entry: "/entry/{id}".
 *
 * This deliberately carries NO topic slug and NO page number. Both of those
 * are properties of the moment the link was copied, not of the entry:
 *
 *   - the slug changes when a topic is renamed or a match date is corrected;
 *   - the page number is a function of how many entries precede this one, and
 *     of the READER's own "entries per page" preference and rookie filter —
 *     the sharer's page 4 is not the recipient's page 4.
 *
 * Baking either one in is what made shared links open at the top of page 1
 * with no anchor to scroll to. The id is the only part that is stable, so the
 * link carries only that and /entry/{id} resolves the rest at open time
 * against the reader's own settings (see EntryPermalinkPage).
 */
export function buildEntryShareUrl(entry: EntryModel): string {
  return toAbsoluteUrl(entryPermalinkPath(entry.id));
}

/**
 * Share link for a whole topic page, at the page the sharer is currently
 * reading. Unlike an entry permalink this one SHOULD carry the page number:
 * "share this topic" from page 4 means page 4, and there is no anchor whose
 * position has to be recomputed.
 *
 * `pageUrl` is expected to be the canonical, id-terminated path ("--{id}"),
 * so the link resolves in one hop instead of bouncing through a 301, and keeps
 * working after the topic is renamed.
 */
export function buildTopicShareUrl(pageUrl: string): string {
  const pageParam = new URLSearchParams(window.location.search).get("sayfa");
  const page = pageParam && Number(pageParam) > 1 ? `?sayfa=${pageParam}` : "";
  return `${toAbsoluteUrl(pageUrl)}${page}`;
}